export async function generateThumbnail(
  imageBlob: Blob,
  maxWidth = 400,
  quality = 0.7,
  rotateLeft = false
): Promise<Blob> {
  // Decode the original image into a bitmap
  const bitmap = await createImageBitmap(imageBlob);

  // Scale factor based on maxWidth while preserving aspect ratio
  const scale = Math.min(1, maxWidth / bitmap.width);
  const targetW = Math.round(bitmap.width * scale);
  const targetH = Math.round(bitmap.height * scale);

  // Prepare canvas (swap dimensions when rotating)
  const canvas = document.createElement('canvas');
  if (rotateLeft) {
    canvas.width = targetH;
    canvas.height = targetW;
  } else {
    canvas.width = targetW;
    canvas.height = targetH;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  // Apply 90° counter-clockwise rotation if required
  if (rotateLeft) {
    ctx.translate(0, canvas.height);
    ctx.rotate(-Math.PI / 2);
  }

  // Draw the (scaled) bitmap
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  // Export to JPEG blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to generate JPEG blob'));
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Convert a Blob (or File) into a Uint8Array – useful before uploading to S3.
 */
export function blobToUint8Array(blob: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else {
        reject(new Error('Failed to read blob as ArrayBuffer'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read blob as ArrayBuffer'));
    reader.readAsArrayBuffer(blob);
  });
} 